import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, Settings, X } from "lucide-react";
import { Link } from "react-router-dom";

const CONSENT_KEY = "proxima_cookie_consent_v1";

const defaultPrefs = {
  essential: true,    // Always on, cannot be disabled
  analytics: false,
  marketing: false,
  ai_improvement: false,
  timestamp: null,
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(CONSENT_KEY);
      if (saved) return;
      // Auto-accept essentials only for authenticated users (they already agreed to ToS on signup)
      // Avoids overlapping the banner with admin/political-intel action buttons.
      const sessionToken = localStorage.getItem("proxima_session_token") || localStorage.getItem("proxima_token");
      if (sessionToken) {
        const auto = { essential: true, analytics: false, marketing: false, ai_improvement: false, timestamp: new Date().toISOString() };
        localStorage.setItem(CONSENT_KEY, JSON.stringify(auto));
        return;
      }
      setTimeout(() => setVisible(true), 1500);
    } catch (e) { /* localStorage unavailable */ }
  }, []);

  const save = (acceptedPrefs) => {
    const final = { ...acceptedPrefs, essential: true, timestamp: new Date().toISOString() };
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(final));
    } catch (e) { /* ignore */ }
    setPrefs(final);
    setVisible(false);
    setShowSettings(false);
    // Trigger a global event so analytics/marketing modules can react
    window.dispatchEvent(new CustomEvent("cookie-consent-updated", { detail: final }));
  };

  const acceptAll = () => save({ essential: true, analytics: true, marketing: true, ai_improvement: true });
  const acceptEssential = () => save({ essential: true, analytics: false, marketing: false, ai_improvement: false });
  const saveCustom = () => save(prefs);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 24 }}
          className="fixed bottom-4 left-4 right-4 md:left-6 md:right-6 lg:max-w-4xl lg:left-1/2 lg:-translate-x-1/2 z-[9999]"
          data-testid="cookie-consent-banner"
        >
          <div className="bg-[#0A0A0F]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-purple-500/10 overflow-hidden">
            {!showSettings ? (
              <div className="p-5 md:p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/15 flex items-center justify-center flex-shrink-0">
                    <Cookie className="w-5 h-5 text-purple-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-white mb-1">Cookies y privacidad</h3>
                    <p className="text-xs text-gray-400 leading-relaxed">
                      Usamos cookies esenciales para autenticarte y operar la plataforma. Tu eliges si activar las opcionales (analitica, marketing, mejora de IA).
                      Consulta nuestra <Link to="/privacy-policy" className="text-purple-300 hover:underline">Politica de Privacidad</Link> y <Link to="/cookie-policy" className="text-purple-300 hover:underline">Politica de Cookies</Link>.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4 justify-end">
                  <button
                    type="button"
                    onClick={() => setShowSettings(true)}
                    className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-300 text-xs hover:bg-white/10 transition-colors flex items-center gap-2"
                    data-testid="cookie-settings"
                  >
                    <Settings className="w-3.5 h-3.5" />Personalizar
                  </button>
                  <button
                    type="button"
                    onClick={acceptEssential}
                    className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-300 text-xs hover:bg-white/10 transition-colors"
                    data-testid="cookie-essential"
                  >
                    Solo esenciales
                  </button>
                  <button
                    type="button"
                    onClick={acceptAll}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-medium hover:opacity-90 transition-opacity"
                    data-testid="cookie-accept-all"
                  >
                    Aceptar todas
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-5 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-white">Preferencias de cookies</h3>
                  <button type="button" onClick={() => setShowSettings(false)} className="text-gray-500 hover:text-white" data-testid="cookie-settings-close">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-3">
                  <CookieToggle label="Esenciales" desc="Necesarias para autenticarte y operar la plataforma. No se pueden desactivar." enabled={true} disabled={true} onChange={() => {}} />
                  <CookieToggle label="Analitica" desc="Nos ayudan a entender como usas la plataforma para mejorarla (datos agregados)." enabled={prefs.analytics} onChange={(v) => setPrefs({ ...prefs, analytics: v })} testId="toggle-analytics" />
                  <CookieToggle label="Marketing" desc="Permitir que recibamos comunicaciones comerciales relevantes." enabled={prefs.marketing} onChange={(v) => setPrefs({ ...prefs, marketing: v })} testId="toggle-marketing" />
                  <CookieToggle label="Mejora de IA" desc="Permitir que tus interacciones (anonimas) ayuden a mejorar los modelos de IA. Tus datos privados nunca se usan." enabled={prefs.ai_improvement} onChange={(v) => setPrefs({ ...prefs, ai_improvement: v })} testId="toggle-ai-improvement" />
                </div>
                <div className="flex gap-2 mt-5 justify-end">
                  <button type="button" onClick={acceptEssential} className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-300 text-xs hover:bg-white/10 transition-colors">Rechazar opcionales</button>
                  <button type="button" onClick={saveCustom} className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-medium hover:opacity-90" data-testid="cookie-save-prefs">Guardar preferencias</button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const CookieToggle = ({ label, desc, enabled, onChange, disabled, testId }) => (
  <div className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
    <button
      type="button"
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      className={`relative w-10 h-6 rounded-full flex-shrink-0 transition-colors ${enabled ? "bg-purple-500" : "bg-white/10"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      data-testid={testId}
    >
      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${enabled ? "translate-x-4" : "translate-x-0.5"}`} />
    </button>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium text-white">{label}</p>
      <p className="text-[11px] text-gray-500 leading-relaxed mt-0.5">{desc}</p>
    </div>
  </div>
);

export default CookieConsent;
